import { APPLICATION_STATE_CHANGED, PUSH_NOTIFICATION_SUPPORTED } from 'constants/mobileEvents'
import ttVersion from '../../version.json'
import type { Stores } from 'contexts/StoresContext'
import type Account from 'mobx/Account/Account'
import type Application from 'mobx/Application/Application'
import type Infra from 'mobx/Infra/Infra'

const WEB_APP_VERSION = ttVersion.version

// Protocol string literals mirror libs-ts/communication-layer/src/ecommerce/index.ts (source of
// truth) - hardcoded here rather than imported so this file has no dependency on that lib.
const EVT_WEB_VIEW_LOADED = 'ecommerce.web.toMobileApp.webViewLoaded.v1'
const EVT_APP_STATE_CHANGE = 'ecommerce.mobileApp.toWeb.appStateChange.v1'
const EVT_ANALYTICS_ACTION = 'ecommerce.web.toMobileApp.analyticsAction.v1'
const METHOD_MOBILE_TO_WEB_GET_VERSION = 'ecommerce.mobileApp.toWeb.getVersion.v1'
const METHOD_WEB_TO_MOBILE_GET_VERSION = 'ecommerce.web.toMobileApp.getVersion.v1'
const METHOD_GET_LOCATION_PERMISSION_STATE = 'ecommerce.web.toMobileApp.getLocationPermissionState.v1'
const METHOD_GET_CURRENT_POSITION = 'ecommerce.web.toMobileApp.getCurrentPosition.v1'

// Action name from the ECOMMERCE_CLIENT_WEB_AND_MOBILE_APP contract (libs-ts/analytics-manager
// ecommerce-core.ts). Lion's analytics preset maps it to Braze's requestPushPermission, which
// shows the OS push-permission popup and registers the token with Braze - the mobile binary
// already ships this, so no new protocol method is needed, only this existing analyticsAction event.
const PUSH_OPT_IN_ACTION_NAME = 'pushNotificationOptedIn'

type LocationPermissionState = 'granted' | 'denied' | 'prompt'

type CurrentPositionResult =
	| { status: 'granted'; position: { latitude: number; longitude: number; accuracy: number | null } }
	| { status: 'denied' }
	| { status: 'unavailable' }

type Envelope =
	| { t: 'hello'; v: number }
	| { t: 'event'; id: string; name: string; payload: unknown }
	| { t: 'req'; id: string; method: string; params: unknown }
	| { t: 'res'; id: string; ok: true; result?: unknown }
	| { t: 'res'; id: string; ok: false; error: { code: string; message: string } }

type ReactNativeWebViewWindow = Window & {
	ReactNativeWebView?: {
		postMessage: (message: string) => void
	}
}

type MobileApplicationEventHandler = (
	stores: Stores,
	Infra: Infra,
	Account: Account,
	Application: Application
) => (mobileAppEvent: unknown) => Promise<void>

interface MobileApplicationLike {
	handleMobileApplicationEvent: MobileApplicationEventHandler
	setPushNotificationsStatus: (value: string) => void
}

let initialized = false
let connected = false
let webView: ReactNativeWebViewWindow['ReactNativeWebView'] | null = null
let mobileApplication: MobileApplicationLike | null = null

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const isLionEnvelope = (value: unknown): value is Envelope => {
	if (!isRecord(value) || typeof value.t !== 'string') {
		return false
	}

	switch (value.t) {
		case 'hello':
			return typeof value.v === 'number'
		case 'event':
			return typeof value.id === 'string' && typeof value.name === 'string'
		case 'req':
			return typeof value.id === 'string' && typeof value.method === 'string'
		case 'res':
			return typeof value.id === 'string' && typeof value.ok === 'boolean'
		default:
			return false
	}
}

const parseEnvelope = (data: unknown): Envelope | null => {
	if (typeof data === 'string') {
		try {
			const parsed = JSON.parse(data)
			return isLionEnvelope(parsed) ? parsed : null
		} catch {
			return null
		}
	}

	return isLionEnvelope(data) ? data : null
}

export const isLionMessage = (data: unknown): boolean => parseEnvelope(data) !== null

const generateEnvelopeId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

const send = (envelope: Envelope) => {
	if (!webView) {
		return
	}

	try {
		webView.postMessage(JSON.stringify(envelope))
	} catch (error) {
		console.error('[Channel:LegacyWeb] Failed to send message', error)
	}
}

interface PendingRequest {
	resolve: (value: unknown) => void
	reject: (error: Error) => void
	timeoutId: ReturnType<typeof setTimeout>
}

const pendingRequests = new Map<string, PendingRequest>()

const DEFAULT_REQUEST_TIMEOUT_MS = 30_000
// Mirrors apps-nextjs/ecommerce/src/lib/geolocation.ts - the native request may span an OS
// permission dialog waiting on the user, so it needs far more than the default request timeout.
const GET_CURRENT_POSITION_TIMEOUT_MS = 5 * 60 * 1000

const request = (method: string, params: unknown, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS): Promise<unknown> =>
	new Promise((resolve, reject) => {
		const id = generateEnvelopeId()
		const timeoutId = setTimeout(() => {
			pendingRequests.delete(id)
			reject(new Error(`Request ${id} timed out after ${timeoutMs}ms`))
		}, timeoutMs)
		pendingRequests.set(id, { resolve, reject, timeoutId })
		send({ t: 'req', id, method, params })
	})

const handleResponse = (envelope: Extract<Envelope, { t: 'res' }>): boolean => {
	const pending = pendingRequests.get(envelope.id)

	if (!pending) {
		return false
	}

	clearTimeout(pending.timeoutId)
	pendingRequests.delete(envelope.id)

	if (envelope.ok) {
		pending.resolve(envelope.result)
	} else {
		pending.reject(new Error(envelope.error?.message ?? 'Request failed'))
	}

	return true
}

export const LionCommunicationLayer = {
	init: (stores: Stores, Infra: Infra, Account: Account, Application: Application, MobileApplication: MobileApplicationLike) => {
		const currentWebView = (window as ReactNativeWebViewWindow).ReactNativeWebView

		if (!currentWebView || initialized) {
			return
		}

		initialized = true
		webView = currentWebView
		mobileApplication = MobileApplication

		const dispatch = (eventName: string, dataObject: unknown) => {
			MobileApplication.handleMobileApplicationEvent(
				stores,
				Infra,
				Account,
				Application
			)({
				data: { eventName, dataObject },
			}).catch((error) => {
				console.error(`[Channel:LegacyWeb] Failed to handle ${eventName}`, error)
			})
		}

		const handleIncomingMessage = (event: Event) => {
			const data = 'data' in event ? event.data : undefined
			const envelope = parseEnvelope(data)

			if (!envelope) {
				return
			}

			if (envelope.t === 'hello') {
				if (!connected) {
					connected = true
					send({ t: 'hello', v: 1 })
					send({ t: 'event', id: generateEnvelopeId(), name: EVT_WEB_VIEW_LOADED, payload: { timestamp: new Date().toISOString() } })

					request(METHOD_WEB_TO_MOBILE_GET_VERSION, undefined)
						.then((result) => {
							const version = isRecord(result) ? result.version : undefined
							console.log('[Channel:LegacyWeb] Received version from mobile app:', version)
						})
						.catch((error) => {
							console.error('[Channel:LegacyWeb] Failed to get version from mobile app', error)
						})

					// A connected peer is the Lion app, whose binary already ships Braze mobile push -
					// the old protocol's PUSH_NOTIFICATION_SUPPORTED announcement is implied by the handshake.
					dispatch(PUSH_NOTIFICATION_SUPPORTED, {})
				}

				return
			}

			if (envelope.t === 'req' && envelope.method === METHOD_MOBILE_TO_WEB_GET_VERSION) {
				send({ t: 'res', id: envelope.id, ok: true, result: { version: WEB_APP_VERSION } })
				return
			}

			if (envelope.t === 'res' && handleResponse(envelope)) {
				return
			}

			if (envelope.t === 'event' && envelope.name === EVT_APP_STATE_CHANGE) {
				dispatch(APPLICATION_STATE_CHANGED, envelope.payload)
			}
		}

		window.addEventListener('message', handleIncomingMessage, false)
	},

	askForPushNotifications: () => {
		if (!connected) {
			return
		}

		send({
			t: 'event',
			id: generateEnvelopeId(),
			name: EVT_ANALYTICS_ACTION,
			payload: { name: PUSH_OPT_IN_ACTION_NAME, data: {}, timestamp: new Date().toISOString() },
		})
		mobileApplication?.setPushNotificationsStatus('accepted')
	},

	isConnected: (): boolean => connected,

	getLocationPermissionState: async (): Promise<LocationPermissionState> => {
		const result = await request(METHOD_GET_LOCATION_PERMISSION_STATE, undefined)
		if (isRecord(result) && typeof result.state === 'string') {
			return result.state as LocationPermissionState
		}
		throw new Error('Malformed getLocationPermissionState response from mobile app')
	},

	getCurrentPosition: (): Promise<CurrentPositionResult> =>
		request(METHOD_GET_CURRENT_POSITION, undefined, GET_CURRENT_POSITION_TIMEOUT_MS) as Promise<CurrentPositionResult>,
}

export default LionCommunicationLayer
